<template>
  <div class="signup">
    <v-form ref="form" v-model="isValid" lazy-validation class="login-form">
      <!-- Profile photo -->
      <div class="field-block">
        <div class="field-label">
          {{ $t("broker.login.newUsers.profilePhoto") }}
        </div>

        <div
          class="upload-box"
          :class="{ 'upload-box--has-image': photoPreviewUrl }"
        >
          <div v-if="photoPreviewUrl" class="upload-box__previewRow">
            <img
              :src="photoPreviewUrl"
              alt="Preview"
              class="upload-box__avatar"
            />

            <div class="upload-box__fileInfo">
              <div class="upload-box__fileName">
                {{
                  selectedFile?.name || $t("broker.login.newUsers.uploadImage")
                }}
              </div>
              <div class="upload-box__fileMeta">
                {{ $t("broker.login.newUsers.size") }} {{ selectedFileSizeLabel || "-" }}
              </div>
              <div class="upload-box__fileMeta">
                {{ $t("broker.login.newUsers.type") }} {{ selectedFileTypeLabel || "JPG, JPEG, PNG" }}
              </div>
              <button
                type="button"
                class="upload-box__changeLink"
                @click="triggerFile"
              >
                {{ $t("broker.login.newUsers.selectImage") }}
                <i class="fa-regular fa-file-arrow-up"></i>
              </button>
            </div>

            <button icon class="upload-box__trashBtn" @click="removePhoto">
              <v-icon size="18">fa-regular fa-trash-can</v-icon>
            </button>
          </div>
          <template v-else>
            <div class="upload-box__left">
              <div class="background-icon">
                <v-icon class="upload-box__icon" small
                  >fa-regular fa-cloud-arrow-up</v-icon
                >
              </div>
              <div class="upload-box__text">
                <div class="upload-box__title">
                  {{ $t("broker.login.newUsers.uploadImage") }}
                </div>
                <div class="upload-box__hint">
                  {{ $t("broker.login.newUsers.dragDrop") }}
                </div>
                <div class="upload-box__meta">
                  {{ $t("broker.login.newUsers.fileTypes") }}
                </div>
              </div>
            </div>
            <div class="upload-box__right">
              <v-btn depressed class="upload-box__btn" @click="triggerFile">
                <v-icon left small>fa-regular fa-folder-open</v-icon>
                {{ $t("broker.login.newUsers.browseFiles") }}
              </v-btn>
            </div>
          </template>

          <input
            ref="fileInput"
            type="file"
            accept="image/png,image/jpeg"
            class="d-none"
            @change="onFileChange"
          />
        </div>
      </div>

      <!-- Names / Lastnames -->
      <v-row dense>
        <v-col cols="12" sm="6">
          <div class="field-block">
            <div class="field-label">
              {{ $t("broker.login.newUsers.name") }}
            </div>
            <v-text-field
              v-model="form.firstName"
              :placeholder="$t('broker.login.newUsers.enterFirstName')"
              outlined
              dense
              :rules="[rules.required]"
              hide-details="auto"
            />
          </div>
        </v-col>

        <v-col cols="12" sm="6">
          <div class="field-block">
            <div class="field-label">
              {{ $t("broker.login.newUsers.lastname") }}
            </div>
            <v-text-field
              v-model="form.lastName"
              :placeholder="$t('broker.login.newUsers.enterLastName')"
              outlined
              dense
              :rules="[rules.required]"
              hide-details="auto"
            />
          </div>
        </v-col>
      </v-row>

      <!-- Country -->
      <div class="field-block">
        <div class="field-label">{{ $t("broker.login.newUsers.country") }}</div>
        <v-autocomplete
          v-model="form.country"
          :items="countries"
          item-text="name"
          item-value="name"
          :placeholder="$t('broker.login.newUsers.selectCountry')"
          :rules="[rules.required]"
          outlined
          dense
          :return-object="false"
        >
          <!-- Render de cada item en la lista -->
          <template v-slot:item="{ item, on, attrs }">
            <v-list-item v-bind="attrs" v-on="on">
              <v-list-item-content>
                <v-list-item-title class="country-item-title">
                  <Flag :code="item.code" :squared="false" class="mr-2" />
                  {{ item.name }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-autocomplete>
      </div>

      <!-- Next -->
      <v-btn
        block
        depressed
        class="next-btn"
        :disabled="!canContinue"
        @click="onNext"
      >
        {{ $t("broker.login.newUsers.next") }}
      </v-btn>
    </v-form>
  </div>
</template>

<script>
import CountriesData from "../../../../views/Register/countries";
export default {
  name: "SignUpNewUserOne",
  props: {
    brokerName: { type: String, default: "Broker name" },
    invitedEmail: { type: String, default: "" },
  },
  data() {
    return {
      countries: CountriesData,
      isValid: false,
      photoPreviewUrl: null,
      form: {
        email: "",
        photoFile: null,
        firstName: "",
        lastName: "",
        country: null,
      },
      rules: {
        required: (v) => !!v || "Required",
      },
    };
  },
  beforeDestroy() {
    if (this.photoPreviewUrl) {
      URL.revokeObjectURL(this.photoPreviewUrl);
    }
  },
  computed: {
    canContinue() {
      // para el primer paso, normalmente obligas: firstName + lastName + country
      return (
        !!this.form.firstName && !!this.form.lastName && !!this.form.country
      );
    },

    selectedFile() {
      return this.form.photoFile;
    },

    selectedFileSizeLabel() {
      const f = this.form.photoFile;
      if (!f || typeof f.size !== "number") return "";
      const bytes = f.size;
      const kb = bytes / 1024;
      if (kb < 1024) return `${Math.round(kb)} KB`;
      const mb = kb / 1024;
      return `${mb.toFixed(1)} MB`;
    },

    selectedFileTypeLabel() {
      const f = this.form.photoFile;
      if (!f) return "";
      // preferimos mime si existe, si no, usamos extensión
      const mime = (f.type || "").toLowerCase();
      if (mime.includes("png")) return "PNG";
      if (mime.includes("jpeg") || mime.includes("jpg")) return "JPG";
      const name = (f.name || "").toLowerCase();
      const ext = name.split(".").pop();
      return ext ? ext.toUpperCase() : "";
    },
  },
  mounted() {
    this.form.email = this.invitedEmail || "";
  },
  methods: {
    triggerFile() {
      this.$refs.fileInput && this.$refs.fileInput.click();
    },
    onFileChange(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      if (this.photoPreviewUrl) {
        URL.revokeObjectURL(this.photoPreviewUrl);
        this.photoPreviewUrl = null;
      }
      this.form.photoFile = file;
      this.photoPreviewUrl = URL.createObjectURL(file);
      e.target.value = "";
    },
    removePhoto() {
      if (this.photoPreviewUrl) {
        URL.revokeObjectURL(this.photoPreviewUrl);
        this.photoPreviewUrl = null;
      }
      this.form.photoFile = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },
    onNext() {
      // solo UI por ahora: emite el payload al padre
      this.$emit("next", { ...this.form });
    },
    isStep1FormValid() {
      return (
        !!(this.form.firstName && this.form.firstName.trim()) &&
        !!(this.form.lastName && this.form.lastName.trim()) &&
        !!this.form.country
      );
    },
  },
};
</script>

<style lang="scss" scoped>
/* Nota: sin scoped para evitar choques raros con Vuetify si lo prefieres */
.signup-one {
  width: 100%;
}

.field-block {
  margin-bottom: 14px;
}

.field-label {
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 2px;
  color: rgba(0, 0, 0, 0.75);
}

.upload-box {
  border: 1px dashed rgba(0, 0, 0, 0.25);
  border-radius: 10px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.upload-box__left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.background-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 45px;
  width: 45px;
  border-radius: 50%;
  background-color: #ebebeb;
}

.upload-box__icon {
  font-size: 25px !important;
  opacity: 0.7;
}

.upload-box__text {
  min-width: 0;
}

.upload-box__title {
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 2px;
}

.upload-box__hint,
.upload-box__meta {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.55);
  line-height: 1.3;
}

.upload-box__btn {
  border-radius: 10px;
  text-transform: none;
  font-weight: 600;
}

.upload-box--has-image .upload-box__preview {
  width: 100%;
}

.upload-box__preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.upload-box__previewImg {
  max-width: 120px;
  max-height: 80px;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
  background: #f5f5f5;
}

.upload-box__previewActions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.upload-box__removeBtn {
  min-width: auto;
  color: rgba(0, 0, 0, 0.6);
}

/* Estado con imagen: cambia el contenedor */
.upload-box--has-image {
  border-style: solid;
  border-color: rgba(0, 0, 0, 0.12);
  background: #fff;
  padding: 6px;
}

/* Layout horizontal tipo “file item” */
.upload-box__previewRow {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
}

/* Avatar circular */
.upload-box__avatar {
  width: 44px;
  height: 44px;
  border-radius: 999px;
  object-fit: cover;
  flex: 0 0 44px;
}

/* Columna de info */
.upload-box__fileInfo {
  min-width: 0;
  flex: 1;
}

/* Nombre archivo */
.upload-box__fileName {
  font-size: 12px;
  font-weight: 600;
}

/* Meta (size/type) */
.upload-box__fileMeta {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.upload-box__dot {
  opacity: 0.6;
}

/* Link “select a different image” */
.upload-box__changeLink {
  padding: 0;
  border: 0;
  background: transparent;
  color: #1a73e8; /* link azul */
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  display: inline-flex;
  align-items: center;
  gap: 6px; 
}

/* Trash a la derecha */
.upload-box__trashBtn {
  flex: 0 0 auto;
  color: rgba(0, 0, 0, 0.45);
}

.next-btn {
  margin-top: 30px;
  border-radius: 10px;
  text-transform: none;
  font-weight: 700;
}

::v-deep .login-form .v-messages__message {
  margin: 0;
  padding: 0;
}

::v-deep .login-form .v-input--selection-controls {
  margin-top: 0px;
  padding-top: 0px;
}

::v-deep .v-btn.next-btn.v-btn--disabled {
  background-color: #c3ffe3 !important;
}

::v-deep .v-text-field.v-text-field--enclosed .v-text-field__details {
  margin-bottom: 0px;
}

.country-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.country-flag {
  font-size: 16px;
}
</style>
